import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Drama, Flame, Loader2, Share2, Trophy, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuthModal } from "@/context/AuthModalContext";
import { Seo } from "@/components/Seo";

const TOTAL = 5;
const gold = "text-[#C89B3C]";
const primaryBtn =
  "inline-flex items-center justify-center gap-2 rounded-full bg-[#C89B3C] px-6 py-3 font-medium text-black transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const ghostBtn =
  "inline-flex items-center justify-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm transition-colors hover:bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3 text-center">
      <Icon className={`mx-auto h-4 w-4 ${gold}`} aria-hidden="true" />
      <p className="mt-1 font-serif text-2xl font-semibold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function MyStats({ me }) {
  if (!me) return null;
  return (
    <div className="mt-6 grid grid-cols-3 gap-3" data-testid="trivia-stats">
      <Stat icon={Flame} label="Day streak" value={me.current_streak} />
      <Stat icon={Trophy} label="Best streak" value={me.longest_streak} />
      <Stat icon={Check} label="Quizzes played" value={me.completed} />
    </div>
  );
}

function shareText(verdicts, questions, score) {
  const grid = questions.map((q) => (verdicts[q.id]?.correct ? "\u{1F7E9}" : "\u{1F7E5}")).join("");
  return `I scored ${score}/${TOTAL} on today's My Theatre Trivia \u{1F3AD}\n${grid}\nCan you beat me? ${window.location.origin}/trivia`;
}

export default function Trivia() {
  const { user } = useAuth();
  const { openAuthModal } = useAuthModal();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [phase, setPhase] = useState("intro"); // intro | question | feedback | done
  const [idx, setIdx] = useState(0);
  const [choice, setChoice] = useState(null);
  const [verdicts, setVerdicts] = useState({}); // question id -> {choice, correct, correct_index, explanation}
  const [busy, setBusy] = useState(false);
  const [me, setMe] = useState(null);
  const headingRef = useRef(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const r = await api.get("/trivia/today");
      const d = r.data;
      setData(d);
      setMe(d.me || null);
      const v = {};
      (d.answered || []).forEach((a) => {
        v[a.question_id] = a;
      });
      setVerdicts(v);
      setChoice(null);
      if (d.status === "completed") {
        setPhase("done");
      } else if (d.status === "in_progress") {
        setIdx((d.answered || []).length);
        setPhase("question");
      } else {
        setIdx(0);
        setPhase("intro");
      }
    } catch (e) {
      setError(apiError(e.response?.data?.detail) || "We couldn't load today's quiz. Please try again.");
    }
  }, []);

  // Reload when someone signs in or out so the page matches their account.
  useEffect(() => {
    load();
  }, [load, user?.id]);

  // Move keyboard focus to the new heading when the step changes.
  useEffect(() => {
    headingRef.current?.focus();
  }, [phase, idx]);

  const questions = data?.questions || [];
  const q = questions[idx];
  const score = questions.filter((x) => verdicts[x.id]?.correct).length;

  const submit = async () => {
    if (choice === null || busy || !q) return;
    setBusy(true);
    try {
      const r = await api.post("/trivia/answer", { question_id: q.id, choice });
      const res = r.data;
      setVerdicts((prev) => ({
        ...prev,
        [q.id]: { question_id: q.id, choice: res.already_answered ? res.choice : choice, correct: res.correct,
                  correct_index: res.correct_index, explanation: res.explanation },
      }));
      if (res.me) setMe(res.me);
      setPhase("feedback");
    } catch (e) {
      const status = e.response?.status;
      toast.error(apiError(e.response?.data?.detail) || "That didn't save. Please try again.");
      if (status === 409) load();
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    setChoice(null);
    if (idx + 1 < TOTAL) {
      setIdx(idx + 1);
      setPhase("question");
    } else {
      setPhase("done");
    }
  };

  const share = async () => {
    const text = shareText(verdicts, questions, score);
    try {
      if (navigator.share) {
        await navigator.share({ text });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast.success("Copied. Paste it to challenge your friends.");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't share. You can copy your score by hand instead.");
    }
  };

  const signIn = () => openAuthModal(() => load(), "Sign in to keep your score and build a daily streak.");

  // ---------------- screens ----------------
  let body;
  if (error) {
    body = (
      <div className="mt-8 rounded-2xl border border-border bg-card p-6 text-center" role="alert">
        <p>{error}</p>
        <button className={`${ghostBtn} mt-4`} onClick={load}>Try again</button>
      </div>
    );
  } else if (!data) {
    body = (
      <div className="flex min-h-[30vh] items-center justify-center" aria-busy="true">
        <Loader2 className={`h-6 w-6 animate-spin ${gold}`} aria-label="Loading today's quiz" />
      </div>
    );
  } else if (!data.available) {
    body = (
      <div className="mt-8 rounded-2xl border border-dashed border-border bg-card p-8 text-center" data-testid="trivia-unavailable">
        <Drama className={`mx-auto h-8 w-8 ${gold}`} aria-hidden="true" />
        <h2 ref={headingRef} tabIndex={-1} className="mt-3 font-serif text-xl font-semibold outline-none">
          Today's quiz isn't ready yet
        </h2>
        <p className="mt-2 text-muted-foreground">The curtain is still down. Please check back a little later.</p>
      </div>
    );
  } else if (phase === "intro") {
    body = (
      <div className="mt-8 rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="trivia-intro">
        <Drama className={`h-8 w-8 ${gold}`} aria-hidden="true" />
        <h2 ref={headingRef} tabIndex={-1} className="mt-3 font-serif text-2xl font-semibold outline-none">
          Today's quiz is ready
        </h2>
        <p className="mt-2 text-muted-foreground">
          Five questions. One go a day. A brand new quiz every day at midnight UK time.
        </p>
        <button className={`${primaryBtn} mt-6`} onClick={() => setPhase("question")} data-testid="trivia-start">
          Play today's quiz <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
        {!user && (
          <p className="mt-5 text-sm text-muted-foreground">
            You can play without an account.{" "}
            <button className="underline underline-offset-2 hover:text-foreground" onClick={signIn}>
              Sign in
            </button>{" "}
            to save your score and build a streak.
          </p>
        )}
        <MyStats me={me} />
      </div>
    );
  } else if (phase === "done") {
    const v = (x) => verdicts[x.id];
    body = (
      <div className="mt-8" data-testid="trivia-results">
        <div className="rounded-2xl border border-border bg-card p-6 text-center sm:p-8">
          <Trophy className={`mx-auto h-8 w-8 ${gold}`} aria-hidden="true" />
          <h2 ref={headingRef} tabIndex={-1} className="mt-3 font-serif text-3xl font-semibold outline-none">
            You scored {score}/{TOTAL}
          </h2>
          <p className="mt-2 text-muted-foreground">
            {score === TOTAL ? "A perfect house. Take a bow." : score >= 3 ? "A solid performance." : "Every great actor started somewhere."}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button className={primaryBtn} onClick={share} data-testid="trivia-share">
              <Share2 className="h-4 w-4" aria-hidden="true" /> Challenge a friend
            </button>
            <Link to="/find-a-show" className={ghostBtn}>Find a show to see</Link>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">Come back tomorrow for a new quiz.</p>
          {!user && (
            <p className="mt-3 text-sm text-muted-foreground">
              <button className="underline underline-offset-2 hover:text-foreground" onClick={signIn}>Sign in</button>{" "}
              to keep your score and start a streak.
            </p>
          )}
          <MyStats me={me} />
        </div>

        <h3 className="mt-8 font-serif text-xl font-semibold">Your answers</h3>
        <ol className="mt-3 space-y-3">
          {questions.map((x, i) => (
            <li key={x.id} className="rounded-xl border border-border bg-card p-4">
              <p className="flex items-start gap-2 font-medium">
                {v(x)?.correct ? (
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" aria-label="Correct" />
                ) : (
                  <X className="mt-0.5 h-5 w-5 shrink-0 text-red-500" aria-label="Incorrect" />
                )}
                <span>{i + 1}. {x.question}</span>
              </p>
              {v(x) && (
                <div className="mt-2 pl-7 text-sm">
                  {!v(x).correct && (
                    <p className="text-muted-foreground">You said: {x.options[v(x).choice]}</p>
                  )}
                  <p>Answer: <span className="font-medium">{x.options[v(x).correct_index]}</span></p>
                  {v(x).explanation && <p className="mt-1 text-muted-foreground">{v(x).explanation}</p>}
                </div>
              )}
            </li>
          ))}
        </ol>
      </div>
    );
  } else if (q) {
    const v = verdicts[q.id];
    const showing = phase === "feedback" && v;
    body = (
      <div className="mt-8 rounded-2xl border border-border bg-card p-5 sm:p-8" data-testid="trivia-question">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Question {idx + 1} of {TOTAL}</span>
          <span>{q.category}</span>
        </div>
        <div
          className="mt-2 h-1.5 overflow-hidden rounded-full bg-border"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={TOTAL}
          aria-valuenow={idx + 1}
          aria-label={`Question ${idx + 1} of ${TOTAL}`}
        >
          <div className="h-full bg-[#C89B3C] transition-all" style={{ width: `${((idx + (showing ? 1 : 0)) / TOTAL) * 100}%` }} />
        </div>

        <h2 id="trivia-q" ref={headingRef} tabIndex={-1} className="mt-6 font-serif text-xl font-semibold leading-snug outline-none sm:text-2xl">
          {q.question}
        </h2>

        <div role="radiogroup" aria-labelledby="trivia-q" className="mt-5 space-y-3">
          {q.options.map((opt, i) => {
            const picked = (showing ? v.choice : choice) === i;
            const isRight = showing && i === v.correct_index;
            const isWrongPick = showing && picked && !v.correct;
            const state = isRight
              ? "border-emerald-500 bg-emerald-500/10"
              : isWrongPick
              ? "border-red-500 bg-red-500/10"
              : picked
              ? "border-[#C89B3C] bg-[#C89B3C]/10"
              : "border-border hover:bg-background";
            return (
              <button
                key={i}
                type="button"
                role="radio"
                aria-checked={picked}
                disabled={showing || busy}
                onClick={() => setChoice(i)}
                className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${state}`}
                data-testid={`trivia-option-${i}`}
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-current text-xs">
                  {isRight ? <Check className="h-4 w-4" aria-hidden="true" /> : isWrongPick ? <X className="h-4 w-4" aria-hidden="true" /> : "ABCD"[i]}
                </span>
                <span>{opt}</span>
                {isRight && <span className="sr-only"> (correct answer)</span>}
                {isWrongPick && <span className="sr-only"> (your answer, incorrect)</span>}
              </button>
            );
          })}
        </div>

        <div aria-live="polite" className="mt-5">
          {showing && (
            <div data-testid="trivia-feedback">
              <p className={`font-medium ${v.correct ? "text-emerald-500" : "text-red-500"}`}>
                {v.correct ? "Correct!" : "Not quite."}
              </p>
              {v.explanation && <p className="mt-1 text-muted-foreground">{v.explanation}</p>}
            </div>
          )}
        </div>

        <div className="mt-6">
          {showing ? (
            <button className={primaryBtn} onClick={next} data-testid="trivia-next">
              {idx + 1 < TOTAL ? "Next question" : "See my score"} <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : (
            <button className={primaryBtn} onClick={submit} disabled={choice === null || busy} data-testid="trivia-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null} Lock in answer
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
      <Seo
        title="Daily Theatre Trivia"
        description="Five new theatre questions every day. Play today's My Theatre quiz and challenge your friends."
      />
      <p className={`text-sm font-medium uppercase tracking-widest ${gold}`}>Discover</p>
      <h1 className="mt-1 font-serif text-3xl font-semibold sm:text-4xl">Daily Theatre Trivia</h1>
      <p className="mt-2 text-muted-foreground">
        How well do you know the West End? Five questions, new every day.
      </p>
      {body}
    </div>
  );
}